import {
  createFixedRule,
  createFixedRuleVersion,
  FixedRulesApiError,
  getActs,
  getConcepts,
  getFixedRules,
  getFixedRuleVersions,
  setFixedRuleLifecycle,
  type ActContract,
  type ConceptContract,
  type FixedRuleContract,
  type RulePeriodicity,
} from '@urbanik/api-client';
import {
  Button,
  CanonicalCard,
  CanonicalDefinitionList,
  CanonicalFilterBar,
  CanonicalFilterField,
  CanonicalActionMenu,
  CanonicalModal,
  CanonicalNotice,
  CanonicalPageHeader,
  CanonicalTimeline,
  DataTable,
  DropdownItem,
  IconButton,
  EmptyState,
  FormActions,
  PageSizeSelector,
  Pagination,
  ResultsCounter,
  SearchField,
  SelectField,
  StatePanel,
  StatusBadge,
  TextAreaField,
  TextField,
  canonicalStates,
  type DataColumn,
  type SortDirection,
} from '@urbanik/ui';
import {
  CalendarDays,
  FileCheck2,
  GitCompareArrows,
  Ellipsis,
  ArchiveRestore,
  Trash2,
  History,
  ListFilter,
  Plus,
  Tags,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiBase } from './api-base';
import './rules.css';
const periodicities: readonly { value: RulePeriodicity; label: string }[] = [
  { value: 'once', label: 'Una vez' },
  { value: 'monthly', label: 'Mensual' },
  { value: 'quarterly', label: 'Trimestral' },
  { value: 'semiannual', label: 'Semestral' },
  { value: 'annual', label: 'Anual' },
];
const labelPeriod = (value: RulePeriodicity) =>
  periodicities.find((x) => x.value === value)?.label ?? value;
const money = new Intl.NumberFormat('es-EC', { style: 'currency', currency: 'USD' });
type Draft = {
  conceptId: string;
  actId: string;
  amount: string;
  periodicity: RulePeriodicity;
  effectiveFrom: string;
  effectiveTo: string;
  condition: string;
};
const blank: Draft = {
  conceptId: '',
  actId: '',
  amount: '',
  periodicity: 'monthly',
  effectiveFrom: '',
  effectiveTo: '',
  condition: '',
};
export function RulesView({
  canCreate,
  canReadVersions,
  canCreateVersions,
  canLifecycle,
}: {
  canCreate: boolean;
  canReadVersions: boolean;
  canCreateVersions: boolean;
  canLifecycle: boolean;
}) {
  const [items, setItems] = useState<readonly FixedRuleContract[]>([]),
    [concepts, setConcepts] = useState<readonly ConceptContract[]>([]),
    [acts, setActs] = useState<readonly ActContract[]>([]),
    [state, setState] = useState<'loading' | 'empty' | 'error' | 'success'>('loading'),
    [notice, setNotice] = useState(''),
    [open, setOpen] = useState(false),
    [versionBase, setVersionBase] = useState<FixedRuleContract | null>(null),
    [timelineOpen, setTimelineOpen] = useState(false),
    [timeline, setTimeline] = useState<readonly FixedRuleContract[]>([]),
    [timelineState, setTimelineState] = useState<'loading' | 'empty' | 'error' | 'success'>(
      'empty',
    ),
    [leftId, setLeftId] = useState(''),
    [rightId, setRightId] = useState(''),
    [saving, setSaving] = useState(false),
    [draft, setDraft] = useState<Draft>(blank),
    [search, setSearch] = useState(''),
    [period, setPeriod] = useState(''),
    [concept, setConcept] = useState(''),
    [lifecycleFilter, setLifecycleFilter] = useState<'active' | 'trash'>('active'),
    [lifecycleTarget, setLifecycleTarget] = useState<FixedRuleContract | null>(null),
    [lifecycleReason, setLifecycleReason] = useState(''),
    [lifecycleSaving, setLifecycleSaving] = useState(false),
    [sort, setSort] = useState<{ columnId: string; direction: SortDirection }>({
      columnId: 'code',
      direction: 'descending',
    }),
    [page, setPage] = useState(1),
    [pageSize, setPageSize] = useState(10);
  const load = useCallback(() => {
    setState('loading');
    const c = new AbortController();
    Promise.all([
      getFixedRules(apiBase, c.signal),
      getConcepts(apiBase, c.signal),
      getActs(apiBase, c.signal),
    ])
      .then(([r, cs, as]) => {
        setItems(r.items);
        setConcepts(cs.items.filter((x) => x.active));
        setActs(as.items);
        setState(r.items.length ? 'success' : 'empty');
      })
      .catch((e) =>
        setState(e instanceof FixedRulesApiError && e.kind === 'forbidden' ? 'error' : 'error'),
      );
    return () => c.abort();
  }, []);
  useEffect(() => load(), [load]);
  const start = () => {
    setDraft({ ...blank, conceptId: concepts[0]?.id ?? '', actId: acts[0]?.id ?? '' });
    setOpen(true);
  };
  const openTimeline = async (rule: FixedRuleContract) => {
    setTimelineOpen(true);
    setTimelineState('loading');
    try {
      const result = await getFixedRuleVersions(apiBase, rule.id);
      setTimeline(result.items);
      setLeftId(result.items[1]?.id ?? result.items[0]?.id ?? '');
      setRightId(result.items[0]?.id ?? '');
      setTimelineState(result.items.length ? 'success' : 'empty');
    } catch {
      setTimelineState('error');
    }
  };
  const startVersion = (rule: FixedRuleContract) => {
    setVersionBase(rule);
    setDraft({
      conceptId: rule.concept.id,
      actId: rule.act.id,
      amount: rule.amount,
      periodicity: rule.periodicity,
      effectiveFrom: rule.effectiveFrom,
      effectiveTo: rule.effectiveTo ?? '',
      condition: rule.condition,
    });
    setTimelineOpen(false);
    setOpen(true);
  };
  const changeLifecycle = async () => {
    if (!lifecycleTarget) return;
    setLifecycleSaving(true);
    try {
      const annulled = !lifecycleTarget.deletedAt;
      await setFixedRuleLifecycle(apiBase, lifecycleTarget.id, annulled, lifecycleReason);
      setLifecycleTarget(null);
      setLifecycleReason('');
      setNotice(
        annulled
          ? 'Regla enviada a papelera sin borrar su historial.'
          : 'Regla restaurada correctamente.',
      );
      load();
    } catch (error) {
      setNotice(
        error instanceof Error ? error.message : 'No pudimos cambiar el estado de la regla.',
      );
    } finally {
      setLifecycleSaving(false);
    }
  };
  const save = async () => {
    setSaving(true);
    try {
      if (versionBase)
        await createFixedRuleVersion(apiBase, versionBase.id, {
          ...draft,
          effectiveTo: draft.effectiveTo || null,
          expectedVersionNumber: versionBase.versionNumber,
        });
      else await createFixedRule(apiBase, { ...draft, effectiveTo: draft.effectiveTo || null });
      setOpen(false);
      setNotice(
        versionBase
          ? 'Nueva versión creada sin modificar el historial.'
          : 'Regla creada correctamente.',
      );
      load();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : 'No pudimos crear la regla.');
    } finally {
      setSaving(false);
    }
  };
  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('es');
    const value = (r: FixedRuleContract, id: string) =>
      id === 'code'
        ? r.code
        : id === 'concept'
          ? r.concept.name
          : id === 'amount'
            ? r.amount
            : id === 'periodicity'
              ? r.periodicity
              : r.effectiveFrom;
    return items
      .filter(
        (r) =>
          (!q ||
            [r.code, r.concept.name, r.act.number, r.condition].some((v) =>
              v.toLocaleLowerCase('es').includes(q),
            )) &&
          (!period || r.periodicity === period) &&
          (!concept || r.concept.id === concept) &&
          (lifecycleFilter === 'trash' ? Boolean(r.deletedAt) : !r.deletedAt),
      )
      .sort((a, b) => {
        const n = value(a, sort.columnId).localeCompare(value(b, sort.columnId), 'es', {
          numeric: true,
        });
        return sort.direction === 'ascending' ? n : -n;
      });
  }, [concept, items, lifecycleFilter, period, search, sort]);
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize)),
    current = Math.min(page, pages),
    rows = filtered.slice((current - 1) * pageSize, current * pageSize);
  const columns: readonly DataColumn<FixedRuleContract>[] = [
    { id: 'code', header: 'Código', accessor: (r) => r.code, sortable: true },
    {
      id: 'version',
      header: 'Versión',
      accessor: (r) => `v${r.versionNumber}`,
      sortable: true,
      cell: (r) => <StatusBadge tone="info">v{r.versionNumber}</StatusBadge>,
    },
    { id: 'concept', header: 'Concepto', accessor: (r) => r.concept.name, sortable: true },
    {
      id: 'amount',
      header: 'Importe fijo',
      accessor: (r) => r.amount,
      sortable: true,
      cell: (r) => <strong>{money.format(Number(r.amount))}</strong>,
    },
    {
      id: 'periodicity',
      header: 'Periodicidad',
      accessor: (r) => labelPeriod(r.periodicity),
      sortable: true,
    },
    {
      id: 'effectiveFrom',
      header: 'Vigencia',
      accessor: (r) => r.effectiveFrom,
      sortable: true,
      cell: (r) => (
        <span>
          {r.effectiveFrom}
          {r.effectiveTo ? ` — ${r.effectiveTo}` : ' — Indefinida'}
        </span>
      ),
    },
    { id: 'act', header: 'Acta aprobada', accessor: (r) => r.act.number },
    {
      id: 'status',
      header: 'Estado',
      accessor: (r) => (r.deletedAt ? 'Anulada' : 'Vigente'),
      cell: (r) => (
        <div className="rule-lifecycle-status">
          <StatusBadge tone={r.deletedAt ? 'neutral' : 'success'}>
            {r.deletedAt ? 'Anulada' : 'Vigente'}
          </StatusBadge>
          {r.deletedAt && r.lifecycleReason ? <small>{r.lifecycleReason}</small> : null}
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Acciones',
      accessor: () => '',
      cell: (r) => (
        <div className="ui-row-actions">
          {canReadVersions ? (
            <IconButton label={`Ver versiones de ${r.code}`} onClick={() => void openTimeline(r)}>
              <History aria-hidden="true" />
            </IconButton>
          ) : null}
          {canLifecycle ? (
            <CanonicalActionMenu
              icon={<Ellipsis aria-hidden="true" />}
              label={`Más acciones para ${r.code}`}
            >
              <DropdownItem
                icon={
                  r.deletedAt ? (
                    <ArchiveRestore aria-hidden="true" />
                  ) : (
                    <Trash2 aria-hidden="true" />
                  )
                }
                onSelect={() => {
                  setLifecycleReason('');
                  setLifecycleTarget(r);
                }}
              >
                {r.deletedAt ? 'Restaurar' : 'Mover a papelera'}
              </DropdownItem>
            </CanonicalActionMenu>
          ) : null}
        </div>
      ),
    },
  ];
  const reset = (fn: () => void) => {
    fn();
    setPage(1);
  };
  return (
    <section className="rules-page">
      <CanonicalPageHeader
        title="Reglas"
        description="Importes fijos institucionales respaldados por conceptos activos y actas aprobadas."
        actions={
          canCreate ? (
            <Button variant="primary" icon={<Plus aria-hidden="true" />} onClick={start}>
              Nueva regla
            </Button>
          ) : null
        }
      />
      {notice ? (
        <CanonicalNotice tone="info" message={notice} onDismiss={() => setNotice('')} />
      ) : null}
      {state === 'loading' || state === 'error' ? (
        <StatePanel
          state={canonicalStates.find((x) => x.kind === state)!}
          onAction={() => load()}
        />
      ) : (
        <>
          <CanonicalFilterBar>
            <SearchField value={search} onChange={(v) => reset(() => setSearch(v))} />
            <CanonicalFilterField icon={<Tags />}>
              <SelectField
                label="Concepto"
                value={concept}
                onValueChange={(v) => reset(() => setConcept(v))}
                options={[
                  { value: '', label: 'Todos los conceptos' },
                  ...concepts.map((x) => ({ value: x.id, label: x.name })),
                ]}
              />
            </CanonicalFilterField>
            <CanonicalFilterField icon={<CalendarDays />}>
              <SelectField
                label="Periodicidad"
                value={period}
                onValueChange={(v) => reset(() => setPeriod(v))}
                options={[{ value: '', label: 'Todas' }, ...periodicities]}
              />
            </CanonicalFilterField>
            <CanonicalFilterField icon={<Trash2 />}>
              <SelectField
                label="Estado"
                value={lifecycleFilter}
                onValueChange={(value) =>
                  reset(() => setLifecycleFilter(value as 'active' | 'trash'))
                }
                options={[
                  { value: 'active', label: 'Reglas vigentes' },
                  { value: 'trash', label: 'Papelera' },
                ]}
              />
            </CanonicalFilterField>{' '}
            {search || concept || period || lifecycleFilter === 'trash' ? (
              <Button
                icon={<ListFilter />}
                onClick={() => {
                  setSearch('');
                  setConcept('');
                  setPeriod('');
                  setLifecycleFilter('active');
                  setPage(1);
                }}
              >
                Limpiar
              </Button>
            ) : null}
          </CanonicalFilterBar>
          <section className="rules-table-shell">
            <DataTable
              paginate={false}
              rows={rows}
              columns={columns}
              visibleColumnIds={new Set(columns.map((c) => c.id))}
              sort={sort}
              onSort={(columnId) => {
                setSort((s) => ({
                  columnId,
                  direction:
                    s.columnId === columnId && s.direction === 'ascending'
                      ? 'descending'
                      : 'ascending',
                }));
                setPage(1);
              }}
              empty={
                <EmptyState
                  title="Sin reglas"
                  description="No hay reglas que coincidan con los filtros."
                />
              }
            />
            <footer className="ui-data-table-footer">
              <ResultsCounter
                from={filtered.length ? (current - 1) * pageSize + 1 : 0}
                to={filtered.length ? (current - 1) * pageSize + rows.length : 0}
                total={filtered.length}
                noun="reglas"
              />
              <Pagination page={current} pages={pages} onChange={setPage} />
              <PageSizeSelector
                value={pageSize}
                options={[5, 10, 20, 50]}
                onChange={(v) => reset(() => setPageSize(v))}
              />
            </footer>
          </section>
        </>
      )}
      <CanonicalModal
        open={Boolean(lifecycleTarget)}
        title={lifecycleTarget?.deletedAt ? 'Restaurar regla' : 'Mover regla a papelera'}
        description={
          lifecycleTarget?.deletedAt
            ? 'La regla volverá a estar vigente y podrá generar nuevas versiones.'
            : 'La regla quedará anulada, pero todas sus versiones y actas de respaldo se conservarán.'
        }
        size="medium"
        onClose={() => setLifecycleTarget(null)}
      >
        <div className="rules-lifecycle-form">
          {!lifecycleTarget?.deletedAt ? (
            <TextAreaField
              required
              label="Motivo de anulación"
              hint="Entre 5 y 500 caracteres. Quedará registrado en auditoría."
              value={lifecycleReason}
              onChange={(event) => setLifecycleReason(event.currentTarget.value)}
            />
          ) : (
            <p>
              Se restaurará <strong>{lifecycleTarget.code}</strong> sin alterar su línea temporal.
            </p>
          )}
          <FormActions>
            <Button onClick={() => setLifecycleTarget(null)}>Cancelar</Button>
            <Button
              variant="primary"
              disabled={lifecycleSaving}
              icon={lifecycleTarget?.deletedAt ? <ArchiveRestore /> : <Trash2 />}
              onClick={() => void changeLifecycle()}
            >
              {lifecycleSaving
                ? 'Guardando…'
                : lifecycleTarget?.deletedAt
                  ? 'Restaurar regla'
                  : 'Mover a papelera'}
            </Button>
          </FormActions>
        </div>
      </CanonicalModal>{' '}
      <CanonicalModal
        open={timelineOpen}
        title="Línea temporal de la regla"

        description="Consulta el historial y compara dos versiones. Las versiones anteriores permanecen sin cambios."
        onClose={() => setTimelineOpen(false)}
      >
        {timelineState === 'loading' ? (
          <StatePanel state={canonicalStates.find((item) => item.kind === 'loading')!} />
        ) : timelineState === 'error' ? (
          <StatePanel state={canonicalStates.find((item) => item.kind === 'error')!} />
        ) : timelineState === 'empty' ? (
          <StatePanel state={canonicalStates.find((item) => item.kind === 'empty')!} />
        ) : (
          <div className="rule-version-history">
            <CanonicalTimeline
              label="Versiones de la regla"
              events={timeline.map((version) => ({
                id: version.id,
                title: 'Versión ' + version.versionNumber,
                date: version.effectiveFrom,
                icon: <History aria-hidden="true" />,
                description:
                  money.format(Number(version.amount)) +
                  ' · ' +
                  labelPeriod(version.periodicity) +
                  ' · ' +
                  version.effectiveFrom +
                  ' — ' +
                  (version.effectiveTo ?? 'Indefinida'),
                actor: 'Respaldo: ' + version.act.number,
                category: (
                  <StatusBadge
                    tone={version.isCurrent && !version.deletedAt ? 'success' : 'neutral'}
                  >
                    {version.isCurrent ? (version.deletedAt ? 'Anulada' : 'Vigente') : 'Histórica'}
                  </StatusBadge>
                ),
              }))}
            />
            {canCreateVersions && timeline[0] && !timeline[0].deletedAt ? (
              <Button variant="primary" icon={<Plus />} onClick={() => startVersion(timeline[0]!)}>
                Crear nueva versión
              </Button>
            ) : null}
            <CanonicalCard
              className="rule-comparison"
              title="Comparar versiones"
              icon={<GitCompareArrows aria-hidden="true" />}
            >
              <div className="rule-comparison-selectors">
                <SelectField
                  label="Versión de referencia"
                  value={leftId}
                  onValueChange={setLeftId}
                  options={timeline.map((version) => ({
                    value: version.id,
                    label: `v${version.versionNumber}`,
                  }))}
                />
                <SelectField
                  label="Versión comparada"
                  value={rightId}
                  onValueChange={setRightId}
                  options={timeline.map((version) => ({
                    value: version.id,
                    label: `v${version.versionNumber}`,
                  }))}
                />
              </div>
              <div className="rule-comparison-grid">
                {[
                  timeline.find((version) => version.id === leftId),
                  timeline.find((version) => version.id === rightId),
                ].map((version, index) =>
                  version ? (
                    <section
                      key={index + '-' + version.id}
                      className="rule-comparison-panel"
                      aria-label={
                        (index === 0 ? 'Versión de referencia: v' : 'Versión comparada: v') +
                        version.versionNumber
                      }
                    >
                      <div className="rule-comparison-panel__heading">
                        <span>{index === 0 ? 'Referencia' : 'Comparada'}</span>
                        <StatusBadge tone={version.isCurrent ? 'success' : 'neutral'}>
                          v{version.versionNumber}
                        </StatusBadge>
                      </div>
                      <CanonicalDefinitionList
                        columns={1}
                        label={'Detalle de la versión ' + version.versionNumber}
                        items={[
                          { label: 'Importe', value: money.format(Number(version.amount)) },
                          { label: 'Periodicidad', value: labelPeriod(version.periodicity) },
                          {
                            label: 'Vigencia',
                            value:
                              version.effectiveFrom + ' — ' + (version.effectiveTo ?? 'Indefinida'),
                          },
                          { label: 'Condición', value: version.condition },
                          { label: 'Acta de respaldo', value: version.act.number },
                        ]}
                      />
                    </section>
                  ) : null,
                )}
              </div>
            </CanonicalCard>
          </div>
        )}
      </CanonicalModal>
      <CanonicalModal
        open={open}
        title={versionBase ? `Nueva versión de ${versionBase.code}` : 'Nueva regla'}
        description="Solo importe fijo USD. No genera obligaciones ni aplica mora, intereses o fórmulas."
        onClose={() => setOpen(false)}
      >
        <div className="rules-form">
          <SelectField
            required
            label="Concepto activo"
            value={draft.conceptId}
            onValueChange={(conceptId) => setDraft({ ...draft, conceptId })}
            options={concepts.map((x) => ({ value: x.id, label: `${x.code} · ${x.name}` }))}
          />
          <SelectField
            required
            label="Acta aprobada"
            value={draft.actId}
            onValueChange={(actId) => setDraft({ ...draft, actId })}
            options={acts.map((x) => ({ value: x.id, label: x.number }))}
          />
          <TextField
            required
            label="Importe fijo USD"
            type="number"
            min="0.01"
            step="0.01"
            value={draft.amount}
            onChange={(e) =>
              setDraft({ ...draft, amount: Number(e.currentTarget.value).toFixed(2) })
            }
          />
          <SelectField
            required
            label="Periodicidad"
            value={draft.periodicity}
            onValueChange={(periodicity) =>
              setDraft({ ...draft, periodicity: periodicity as RulePeriodicity })
            }
            options={periodicities}
          />
          <TextField
            required
            label="Inicio de vigencia"
            type="date"
            value={draft.effectiveFrom}
            onChange={(e) => setDraft({ ...draft, effectiveFrom: e.currentTarget.value })}
          />
          <TextField
            label="Fin de vigencia (opcional)"
            type="date"
            value={draft.effectiveTo}
            onChange={(e) => setDraft({ ...draft, effectiveTo: e.currentTarget.value })}
          />
          <TextAreaField
            required
            label="Condición de aplicación"
            hint="Describe a quién y cuándo aplica; no admite fórmulas."
            value={draft.condition}
            onChange={(e) => setDraft({ ...draft, condition: e.currentTarget.value })}
          />
          <FormActions>
            <Button onClick={() => setOpen(false)}>Cancelar</Button>
            <Button
              variant="primary"
              disabled={saving}
              icon={<FileCheck2 />}
              onClick={() => void save()}
            >
              {saving ? 'Guardando…' : versionBase ? 'Crear nueva versión' : 'Guardar regla'}
            </Button>
          </FormActions>
        </div>
      </CanonicalModal>
    </section>
  );
}
